import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

const EMPTY = { name: "", state: "", service_type: "", discount_percent: "", description: "", youtube_url: "" };
const YT = /(youtube\.com\/(watch\?(.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)[A-Za-z0-9_-]{11}/;
const SEL = "h-10 w-full rounded-md border border-border bg-background px-3 text-sm";

export const PartnerCompanyForm = ({ editing, meta, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setF(editing.id ? { ...EMPTY, ...editing, discount_percent: String(editing.discount_percent), youtube_url: editing.youtube_url || "" } : EMPTY);
  }, [editing]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const d = Number(String(f.discount_percent).replace(",", "."));
  const errors = {
    discount: f.discount_percent !== "" && (!(d >= meta.min_discount) || d > 90) ? `O desconto mínimo é ${meta.min_discount}% (máximo 90%)` : "",
    youtube: f.youtube_url && !YT.test(f.youtube_url) ? "Link do YouTube inválido" : "",
  };
  const valid = f.name.trim().length >= 2 && f.state && f.service_type && f.discount_percent !== "" && f.description.trim().length >= 2
    && !errors.discount && !errors.youtube;

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, discount_percent: d };
      if (editing.id) await api.put(`/my/partner-companies/${editing.id}`, body);
      else await api.post("/my/partner-companies", body);
      toast.success(editing.id ? "Cadastro atualizado" : "Cadastro criado! Pague a taxa para enviar à aprovação.");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="partner-company-form">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Editar cadastro" : "Cadastrar empresa parceira"}</DialogTitle>
          <DialogDescription>Taxa única de R$ 4,99. O cadastro só aparece na vitrine após aprovação.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Nome do serviço / empresa</Label><Input value={f.name} onChange={set("name")} data-testid="pc-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Estado</Label>
              <select className={SEL} value={f.state} onChange={set("state")} data-testid="pc-state-select">
                <option value="">Selecione</option>{meta.states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div className="space-y-1"><Label>Tipo de serviço</Label>
              <select className={SEL} value={f.service_type} onChange={set("service_type")} data-testid="pc-type-select">
                <option value="">Selecione</option>{meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
          </div>
          <div className="space-y-1"><Label>Desconto oferecido (%)</Label>
            <Input inputMode="decimal" value={f.discount_percent} onChange={set("discount_percent")} placeholder="Mínimo 3" data-testid="pc-discount-input" />
            {errors.discount ? <p className="text-xs text-rose-500" data-testid="pc-discount-error">{errors.discount}</p>
              : d >= 3 && <p className="text-xs text-muted-foreground">1% Conecta Mais · 1% para o patrocinador de quem compra · {(d - 2).toLocaleString("pt-BR")}% de volta para o cliente</p>}
          </div>
          <div className="space-y-1"><Label>Descrição — até 150 caracteres ({f.description.length}/150)</Label>
            <Textarea rows={3} maxLength={150} value={f.description} onChange={set("description")} placeholder="Descreva seu serviço em até 150 caracteres" data-testid="pc-description-input" />
            <p className="text-[11px] text-muted-foreground">Depois de aprovado, você poderá alterar apenas a descrição, sem nova aprovação.</p></div>
          <div className="space-y-1"><Label>Vídeo do YouTube (opcional)</Label>
            <Input value={f.youtube_url} onChange={set("youtube_url")} placeholder="https://youtube.com/watch?v=..." data-testid="pc-youtube-input" />
            {errors.youtube && <p className="text-xs text-rose-500" data-testid="pc-youtube-error">{errors.youtube}</p>}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="gold-btn" disabled={!valid || saving} onClick={save} data-testid="pc-save-btn">{saving ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
